import { configureStore } from '@reduxjs/toolkit';
import { appStatusSlice, appStatusSliceName } from './app-status/app-status.slice';
import { backendsListenerMiddleware, backendsSlice, backendsSliceName } from './backends.slice';
import { connectListenerMiddleware, connectSlice, connectSliceName } from './connect.slice';
import { contextSlice, contextSliceName } from './context.slice';
import { datatoolsName, datatoolsResultMiddleware, datatoolsResultSlice } from './datatools.slice';
import { devtoolsListenerMiddleware, devtoolsSlice, devtoolsSliceName } from './devtools.slice';
import { errorSlice, errorSliceName } from './error/error.slice';
import { flagsSlice, flagsSliceName } from './flags.slice';
import { globalCatchSlice, globalCatchSliceName } from './global-catch.slice';
import {
  importedEventingFunctionListenerMiddleware,
  importedEventingFunctionSlice,
  importedEventingFunctionSliceName,
} from './imported-eventing-function.slice';
import { chatContextSlice, chatContextSliceName } from './iq/chat-context.slice';
import { chatDebugSlice, chatDebugSliceName } from './iq/chat-debug/chat-debug.slice';
import { chatHistoryMiddleware, chatHistoryStoreSlice, chatHistoryStoreSliceName } from './iq/chat-history-store/chat-history-store.slice';
import { chatSettingsMiddleware, chatSettingsSlice, chatSettingsSliceName } from './iq/chat-settings/chat-settings.slice';
import { chatStoreSlice, chatStoreSliceName } from './iq/chat-store/chat-store.slice';
import { flowsSlice, flowsSliceName } from './iq/flows.slice';
import { helpfulMessagesSlice, helpfulMessagesSliceName } from './iq/helpful-message.slice';
import { aiInquirerSlice, aiInquirerSliceName } from './iq/query-ai/query-ai.slice';
import { iQInsightsSlice, iQInsightsSliceName } from './iq-insights.slice';
import { notFoundSlice, notFoundSliceName } from './not-found.slice';
import { notificationsSlice, notificationsSliceName } from './notifications.slice';
import { organizationSlice, organizationSliceName } from './organization.slice';
import { playgroundSlice, playgroundSliceName } from './playground.slice';
import { queryChartMiddleware, queryChartSlice, queryChartSliceName } from './query-chart-state.slice';
import { queryResultMiddleware, queryResultName, queryResultSlice } from './query-result.slice';
import { registrationSlice, registrationSliceName } from './registration.slice';
import { RootState } from './root.types';
import { searchHistoryListenerMiddleware, searchHistorySlice, searchHistorySliceName } from './search-history.slice';
import { serverlessApiKeySlice, serverlessApiKeySliceName } from './serverless-api-key/serverless-api-key.slice';
import { sessionTerminationSlice, sessionTerminationSliceName } from './session-termination.slice';

const storeReducers = {
  [notificationsSliceName]: notificationsSlice.reducer,
  [contextSliceName]: contextSlice.reducer,
  [sessionTerminationSliceName]: sessionTerminationSlice.reducer,
  [backendsSliceName]: backendsSlice.reducer,
  [devtoolsSliceName]: devtoolsSlice.reducer,
  [globalCatchSliceName]: globalCatchSlice.reducer,
  [organizationSliceName]: organizationSlice.reducer,
  [notFoundSliceName]: notFoundSlice.reducer,
  [registrationSliceName]: registrationSlice.reducer,
  [appStatusSliceName]: appStatusSlice.reducer,
  [searchHistorySliceName]: searchHistorySlice.reducer,
  [serverlessApiKeySliceName]: serverlessApiKeySlice.reducer,
  [playgroundSliceName]: playgroundSlice.reducer,
  [flagsSliceName]: flagsSlice.reducer,
  [errorSliceName]: errorSlice.reducer,
  [queryResultName]: queryResultSlice.reducer,
  [queryChartSliceName]: queryChartSlice.reducer,
  [datatoolsName]: datatoolsResultSlice.reducer,
  [importedEventingFunctionSliceName]: importedEventingFunctionSlice.reducer,
  [connectSliceName]: connectSlice.reducer,
  [helpfulMessagesSliceName]: helpfulMessagesSlice.reducer,
  [flowsSliceName]: flowsSlice.reducer,
  [chatContextSliceName]: chatContextSlice.reducer,
  [chatHistoryStoreSliceName]: chatHistoryStoreSlice.reducer,
  [chatStoreSliceName]: chatStoreSlice.reducer,
  [chatSettingsSliceName]: chatSettingsSlice.reducer,
  [aiInquirerSliceName]: aiInquirerSlice.reducer,
  [chatDebugSliceName]: chatDebugSlice.reducer,
  [iQInsightsSliceName]: iQInsightsSlice.reducer,
};

export const store = configureStore({
  reducer: storeReducers,
  middleware: (getDefaultMiddleware) => [
    ...getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
    queryResultMiddleware.middleware,
    backendsListenerMiddleware.middleware,
    devtoolsListenerMiddleware.middleware,
    searchHistoryListenerMiddleware.middleware,
    datatoolsResultMiddleware.middleware,
    queryChartMiddleware.middleware,
    importedEventingFunctionListenerMiddleware.middleware,
    connectListenerMiddleware.middleware,
    chatHistoryMiddleware.middleware,
    chatSettingsMiddleware.middleware,
  ],
});

export const getTestsStore = (initialState?: Partial<RootState>) => {
  return configureStore({
    reducer: storeReducers,
    preloadedState: initialState,
  });
};

document.addEventListener('cp-ui-v3-logout', () => {
  const resetActions = [
    notificationsSlice.actions.reset,
    contextSlice.actions.reset,
    sessionTerminationSlice.actions.reset,
    backendsSlice.actions.reset,
    devtoolsSlice.actions.reset,
    globalCatchSlice.actions.reset,
    registrationSlice.actions.reset,
    appStatusSlice.actions.reset,
    searchHistorySlice.actions.reset,
    playgroundSlice.actions.reset,
    flagsSlice.actions.reset,
    errorSlice.actions.reset,
    importedEventingFunctionSlice.actions.reset,
    connectSlice.actions.reset,
    chatDebugSlice.actions.reset,
    iQInsightsSlice.actions.reset,
  ];

  resetActions.forEach((resetAction) => {
    store.dispatch(resetAction());
  });
});

export type AppDispatch = typeof store.dispatch;
